import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { updateEmail } from 'firebase/auth';
import { db } from './firebase';
import { serverTimestamp } from 'firebase/firestore';

// Handle initial email verification for new signups
export const handleInitialEmailVerification = async (userId) => {
  try {
    console.log('🔍 Starting email verification for user:', userId);
    
    // Simple verification - just confirm the link was clicked
    // No Firestore operations to avoid permission issues
    console.log('✅ Email verification link clicked successfully');
    
    return {
      success: true,
      message: 'Email verification link received. Please sign in to complete verification.',
      requiresSignIn: true
    };
    
  } catch (error) {
    console.error('❌ Error handling initial email verification:', error);
    return {
      success: false,
      message: 'Verification failed. Please try signing in directly.'
    };
  }
};

// Verify email change request
export const verifyEmailChange = async (requestId) => {
  try {
    // Get the verification request
    const requestRef = doc(db, 'emailChangeRequests', requestId);
    const requestSnap = await getDoc(requestRef);
    
    if (!requestSnap.exists()) {
      throw new Error('Verification request not found');
    }
    
    const request = requestSnap.data();
    
    // Check if request has expired
    if (request.expiresAt?.toDate() < new Date()) {
      await deleteDoc(requestRef);
      throw new Error('Verification request has expired');
    }
    
    // Check if request is still pending
    if (request.status !== 'pending') {
      throw new Error('Verification request has already been processed');
    }
    
    // Update the request status to verified
    await updateDoc(requestRef, {
      status: 'verified',
      verifiedAt: new Date()
    });
    
    // Update the user's email in Firestore
    const userRef = doc(db, 'users', request.userId);
    await updateDoc(userRef, {
      email: request.newEmail,
      emailUpdatedAt: new Date()
    });
    
    // Note: Firebase Auth email update requires re-authentication
    // This will need to be handled in the UI when the user is logged in
    
    return {
      success: true,
      message: 'Email change verified successfully',
      newEmail: request.newEmail
    };
  } catch (error) {
    console.error('Error verifying email change:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

// Complete email change in Firebase Auth (requires user to be logged in)
export const completeEmailChange = async (user, newEmail) => {
  try {
    await updateEmail(user, newEmail);
    return {
      success: true,
      message: 'Email updated successfully in authentication system'
    };
  } catch (error) {
    console.error('Error updating email in auth:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

// Cancel email change request
export const cancelEmailChange = async (requestId) => {
  try {
    await deleteDoc(doc(db, 'emailChangeRequests', requestId));
    return {
      success: true,
      message: 'Email change request cancelled'
    };
  } catch (error) {
    console.error('Error cancelling email change:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

// Get pending email change requests for a user
export const getPendingEmailChanges = async (userId) => {
  try {
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    
    const requestsQuery = query(
      collection(db, 'emailChangeRequests'),
      where('userId', '==', userId),
      where('status', '==', 'pending')
    );
    
    const snapshot = await getDocs(requestsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting pending email changes:', error);
    return [];
  }
};

// Migrate existing users to have email verification status
export const migrateExistingUsersToEmailVerification = async () => {
  try {
    const { collection, getDocs, updateDoc } = await import('firebase/firestore');
    
    // Get all users
    const usersSnapshot = await getDocs(collection(db, 'users'));
    let migratedCount = 0;
    let errorCount = 0;
    
    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data();
      
      // Skip if already has email verification status
      if (userData.emailVerified !== undefined) {
        continue;
      }
      
      try {
        // Update user to have email verification status
        await updateDoc(doc(db, 'users', userDoc.id), {
          emailVerified: true, // Assume existing users are verified
          emailVerifiedAt: new Date(),
          migratedAt: new Date()
        });
        
        migratedCount++;
        console.log(`✅ Migrated user: ${userData.email}`);
      } catch (error) {
        errorCount++;
        console.error(`❌ Failed to migrate user ${userData.email}:`, error);
      }
    }
    
    console.log(`Migration complete: ${migratedCount} users migrated, ${errorCount} errors`);
    
    return {
      success: true,
      migratedCount,
      errorCount,
      message: `Successfully migrated ${migratedCount} users to email verification system`
    };
  } catch (error) {
    console.error('Error during migration:', error);
    return {
      success: false,
      message: error.message
    };
  }
}; 

// Update user's MFA preferences
export const updateUserMFAPreferences = async (userId, preferences) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      mfaEnabled: preferences.mfaEnabled,
      preferredMfaType: preferences.preferredMfaType,
      mfaUpdatedAt: serverTimestamp()
    });

    return {
      success: true,
      message: 'MFA preferences updated'
    };
  } catch (error) {
    console.error('Error updating MFA preferences:', error);
    return {
      success: false,
      message: 'Failed to update MFA preferences'
    };
  }
};

// TEMPORARY: Manually verify admin email (for development/admin access)
export const manuallyVerifyAdminEmail = async (email) => {
  try {
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    
    // Find user by email
    const usersQuery = query(
      collection(db, 'users'),
      where('email', '==', email)
    );
    
    const snapshot = await getDocs(usersQuery);
    
    if (snapshot.empty) {
      return {
        success: false,
        message: 'User not found'
      };
    }
    
    const userDoc = snapshot.docs[0];
    const userId = userDoc.id;
    
    // Update email verification status
    await updateDoc(doc(db, 'users', userId), {
      emailVerified: true,
      emailVerifiedAt: new Date(),
      manuallyVerified: true,
      verifiedBy: 'admin_override'
    });
    
    console.log(`✅ Manually verified email for user: ${userId}`);
    
    return {
      success: true,
      message: 'Email manually verified',
      userId: userId
    };
  } catch (error) {
    console.error('Error manually verifying email:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

// Admin function to manually verify Firebase Auth email
export const manuallyVerifyFirebaseAuthEmail = async (userId) => {
  try {
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    
    // Get the user from Firebase Auth
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('No authenticated user found');
    }
    
    // Note: Firebase Auth doesn't allow direct email verification without the user clicking the link
    // However, we can update the user's profile to mark them as verified in our system
    // and bypass the Firebase Auth verification check
    
    // Update the user document to indicate Firebase Auth verification bypass
    await updateDoc(doc(db, 'users', userId), {
      emailVerified: true,
      emailVerifiedAt: new Date(),
      firebaseAuthVerified: true,
      manuallyVerified: true,
      verifiedBy: 'admin_manual_verification'
      // SECURITY: Removed verificationBypass - no bypasses allowed
    });
    
    console.log(`✅ Manually verified Firebase Auth email for user: ${userId}`);
    
    return {
      success: true,
      message: 'Firebase Auth email verification bypassed',
      userId: userId
    };
  } catch (error) {
    console.error('Error manually verifying Firebase Auth email:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

// Admin function to resend verification email
export const resendVerificationEmail = async (userId) => {
  try {
    const { getAuth, sendEmailVerification } = await import('firebase/auth');
    const auth = getAuth();
    
    // Get the user from Firebase Auth
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('No authenticated user found');
    }
    
    // Send verification email
    await sendEmailVerification(user, {
      url: `${window.location.origin}/verify-initial?uid=${user.uid}`,
      handleCodeInApp: false
    });
    
    // Update the verification sent timestamp
    await updateDoc(doc(db, 'users', userId), {
      emailVerificationSentAt: new Date(),
      verificationEmailResent: true,
      lastVerificationEmailSent: new Date()
    });
    
    console.log(`✅ Verification email resent for user: ${userId}`);
    
    return {
      success: true,
      message: 'Verification email sent successfully',
      userId: userId
    };
  } catch (error) {
    console.error('Error resending verification email:', error);
    return {
      success: false,
      message: error.message
    };
  }
}; 